import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, EyeOff, Clock } from "lucide-react";
import { Badge } from "../../components/shared/Badge";
import { PhoneTap } from "../../components/shared/PhoneTap";
import { WhatsAppTap } from "../../components/shared/WhatsAppTap";
import { useAppData } from "../../lib/AppDataContext";
import {
  STATUSES, STATUS_META, isAssignedTo, getAssignees, isOverdue, configLabel,
} from "../../lib/leads";

/* Kanban-style board: one column per status, leads grouped within. The
   board is horizontally scrollable; on desktop we add visible left/right
   arrow buttons plus drag-to-pan on the empty space between columns so
   users don't have to reach for the bottom scrollbar. On touch devices
   the native swipe gesture works without extra plumbing.

   Ported 1:1 from OLD admin 1561-1771. Includes the rAF-batched
   drag-to-pan, edge-fade gradients, and scrollbound arrow visibility. */
export function KanbanBoard({ rows, statusFilter, role, onOpen }) {
  const { allSalesPeople } = useAppData();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let raf = null;
    const check = () => {
      raf = null;
      const left = el.scrollLeft > 4;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
      setCanScrollLeft((prev) => (prev === left ? prev : left));
      setCanScrollRight((prev) => (prev === right ? prev : right));
    };
    const onScroll = () => {
      if (raf == null) raf = requestAnimationFrame(check);
    };
    check();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (raf != null) cancelAnimationFrame(raf);
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [rows.length, statusFilter]);

  const dragStateRef = useRef({ down: false, startX: 0, startScroll: 0, lastX: 0, raf: null });
  const onPointerDown = (e) => {
    if (e.target.closest(".kanban-card")) return;
    const el = scrollRef.current;
    if (!el) return;
    dragStateRef.current = { down: true, startX: e.clientX, startScroll: el.scrollLeft, lastX: e.clientX, raf: null };
    el.style.cursor = "grabbing";
    try { el.setPointerCapture?.(e.pointerId); } catch { /* unsupported, ignore */ }
  };
  const onPointerMove = (e) => {
    const st = dragStateRef.current;
    if (!st.down) return;
    st.lastX = e.clientX;
    if (st.raf != null) return;
    st.raf = requestAnimationFrame(() => {
      st.raf = null;
      const el = scrollRef.current;
      if (!el || !dragStateRef.current.down) return;
      el.scrollLeft = st.startScroll - (st.lastX - st.startX);
    });
  };
  const endDrag = (e) => {
    const st = dragStateRef.current;
    if (st.raf != null) { cancelAnimationFrame(st.raf); st.raf = null; }
    st.down = false;
    const el = scrollRef.current;
    if (el) {
      el.style.cursor = "";
      try { if (e?.pointerId != null) el.releasePointerCapture?.(e.pointerId); } catch { /* ignore */ }
    }
  };

  const COLUMN_STEP = 296;
  const scrollBy = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * COLUMN_STEP * 2, behavior: "smooth" });
  };

  const cols = statusFilter === "all" ? STATUSES : [statusFilter];
  const byStatus = STATUSES.reduce((acc, s) => (acc[s] = [], acc), {});
  rows.forEach((l) => { if (byStatus[l.status]) byStatus[l.status].push(l); });

  return (
    <div className="relative">
      {canScrollLeft && (
        <button
          type="button"
          onClick={() => scrollBy(-1)}
          className="hidden md:grid place-items-center absolute left-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white shadow-lg ring-1 ring-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors"
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {canScrollRight && (
        <button
          type="button"
          onClick={() => scrollBy(1)}
          className="hidden md:grid place-items-center absolute right-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white shadow-lg ring-1 ring-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors"
          aria-label="Scroll right"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}

      {canScrollLeft && <div className="hidden md:block absolute left-0 top-0 bottom-0 w-12 z-[1] pointer-events-none bg-gradient-to-r from-white to-transparent" />}
      {canScrollRight && <div className="hidden md:block absolute right-0 top-0 bottom-0 w-12 z-[1] pointer-events-none bg-gradient-to-l from-white to-transparent" />}

      <div
        ref={scrollRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        className="kanban-scroll overflow-x-auto overflow-y-hidden p-4 cursor-grab select-none"
        style={{ scrollSnapType: "x proximity", scrollbarWidth: "thin" }}
      >
        <div className="flex gap-2 min-w-max pb-1">
          {cols.map((s) => {
            const colLeads = byStatus[s] || [];
            const meta = STATUS_META[s];
            return (
              <div key={s} className="w-72 shrink-0 flex flex-col" style={{ scrollSnapAlign: "start" }}>
                <div className={`rounded-t-xl px-3.5 py-3 ring-1 ring-inset ${meta.cls} flex items-center justify-between`}>
                  <span className="text-sm font-bold tracking-tight uppercase">{meta.label}</span>
                  <span className="text-sm font-bold tabular-nums bg-white/70 rounded-full min-w-[28px] h-7 px-2 grid place-items-center ring-1 ring-inset ring-current/10">
                    {colLeads.length}
                  </span>
                </div>
                {/* Each column scrolls independently. Previously this had
                    min-h with no cap, so a column with 76 leads grew to full
                    height and reaching its bottom meant scrolling the whole
                    page past every other column.

                    max-h is viewport-relative so the board fits the screen
                    regardless of laptop size; the outer wrapper keeps
                    overflow-y-hidden so only the columns scroll. */}
                <div className="flex-1 rounded-b-xl bg-slate-50/60 ring-1 ring-inset ring-slate-200 p-2 space-y-2 min-h-[200px] max-h-[calc(100vh-320px)] overflow-y-auto kanban-col-scroll">
                  {colLeads.length === 0 ? (
                    <div className="text-[11px] text-slate-400 text-center py-10">No leads</div>
                  ) : colLeads.map((l) => {
                    const assignedToMe = isAssignedTo(l, "s1");
                    const showFull = role === "super_admin" || role === "admin" || assignedToMe;
                    const overdue = isOverdue(l);
                    const cardCls = overdue
                      ? "p-3 ring-2 ring-red-300 bg-red-50 hover:bg-red-100 cursor-pointer transition-colors"
                      : "p-3 bg-white ring-1 ring-slate-200 hover:bg-slate-50 cursor-pointer transition-colors";
                    const waNumber = l.whatsapp || l.mobile;
                    const assigneeIds = getAssignees(l);
                    const firstOwner = assigneeIds.length > 0 ? allSalesPeople.find((s) => s.id === assigneeIds[0]) : null;
                    const extraOwners = Math.max(0, assigneeIds.length - 1);
                    return (
                      <div key={l.id} className={`kanban-card rounded-lg ${cardCls}`}>
                        <div onClick={() => onOpen(l)}>
                          <div className="font-semibold text-sm text-slate-900 truncate">{l.name}</div>
                          <div className="text-xs text-slate-500 truncate mt-0.5">{l.project || "—"}</div>
                          {l.config && (Array.isArray(l.config) ? l.config.length > 0 : true) && (
                            <div className="text-xs text-slate-600 mt-2">{configLabel(l.config)}{l.budget ? " · " + l.budget : ""}</div>
                          )}
                          <div className="flex flex-wrap items-center gap-1 mt-2">
                            <Badge cls="bg-slate-100 text-slate-600 ring-slate-400/20">{l.source}</Badge>
                            {firstOwner
                              ? <Badge cls="bg-blue-50 text-blue-700 ring-blue-600/20">{firstOwner.name.split(" ")[0]}{extraOwners > 0 ? ` +${extraOwners}` : ""}</Badge>
                              : <Badge cls="bg-amber-50 text-amber-700 ring-amber-600/20">Unassigned</Badge>}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-2 pt-2 border-t border-slate-100">
                          {showFull
                            ? <PhoneTap mobile={l.mobile} display="Call" className="text-xs" />
                            : <span className="inline-flex items-center gap-1 text-[11px] text-slate-400"><EyeOff className="w-3 h-3" />Masked</span>}
                          {showFull && <WhatsAppTap mobile={waNumber} label="WhatsApp" className="text-xs" />}
                        </div>
                        {l.followUpAt && (
                          <div className={`mt-2 text-[11px] flex items-center gap-1 ${overdue ? "text-red-700 font-semibold" : "text-slate-500"}`}>
                            <Clock className="w-3 h-3" />
                            {overdue ? "Overdue: " : ""}
                            {l.followUpAt.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} &middot; {l.followUpAt.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="md:hidden text-[11px] text-slate-400 text-center pb-2 -mt-1 pointer-events-none">
        Swipe sideways to see more columns
      </div>
    </div>
  );
}
